/**
 * THE DOMAIN PROJECTION — consumer state in, domain state out.
 *
 * FE1-W0 Authorization Record §3.8 places "domain projection" in the Capability
 * Definition Read Slice. FE-1 Stage Work Item §7.5 fixes what it may be:
 *
 *     DOMAIN STATE  owned by the domain layer; derived ONLY from frozen contract
 *                   semantics; deterministic given the same server state
 *
 * It DERIVES. It does not fetch, does not read a contract, does not reach an
 * adapter, does not perform I/O, does not read a clock, does not consult a random
 * source and does not consult ambient or global state. `projectCapabilityDefinition`
 * is a pure function of its argument, so the same consumer state always yields the
 * same domain state.
 *
 * WHAT IT REFUSES TO TRANSLATE — the whole point of the layer
 *
 *   AVAILABLE   is NOT translated into operational availability, readiness,
 *               assignment, commitment or eligibility. It means a snapshot was
 *               retrieved, so the definition is READABLE. Nothing more.
 *   UNKNOWN     is NOT translated into empty, error, default, loading or success.
 *               It stays its own state.
 *   UNAVAILABLE is NOT translated into unknown, empty or success, and NO
 *               implementation diagnostic is carried — the frozen read seam
 *               publishes none, and this layer has no slot for one.
 *   PUBLISHED   is NOT translated into ready. DRAFT and DEACTIVATED are NOT
 *               translated into eligibility or execution permission. See
 *               `./lifecycle.ts`.
 *   NON_AUTHORITY is carried as the fixed property it is. Nothing here renders a
 *               read result as permission to act.
 *
 * IT CONSUMES THE PHASE A SEMANTIC SYSTEM AND ADDS NOTHING TO IT
 *   `frontend/src/shared/semantic/**` is the single authority for the Unknown / Empty /
 *   Loading / Error / Stale / Degraded / Unavailable / Non-Authority separations
 *   (FE-1 Stage Work Item §4.1 E). This module calls its `matchObservation`, which
 *   requires a handler for every observation variant. It does NOT define a second
 *   `UnknownState`, a second authority value, a success predicate, an elevation
 *   function, or a new semantic member.
 *
 * THE FRESHNESS THE FROZEN READ PLANE DOES NOT PUBLISH IS CARRIED, NOT INVENTED
 *   The consumer layer supplies `Freshness.UNKNOWN` for every outcome, because the
 *   frozen result carries no currency (recorded finding `FE1-W0B-F-02`). The
 *   projection PASSES THAT THROUGH UNCHANGED. It does not upgrade it to `FRESH`,
 *   which would assert a currency nothing established, and it does not downgrade
 *   it to `STALE` or `DEGRADED`, which would assert a diminution nothing
 *   established. The caveat stays visible to the presentation rather than being
 *   dropped at the domain boundary.
 */

import { matchObservation, type ReadModelObservation } from '../../../../shared/semantic/index';
import type { CapabilityDefinitionSnapshotV1 } from '../consumer';
import {
  definitionReadNotCompleted,
  definitionReadUnavailable,
  definitionReadable,
  definitionUnknown,
  type CapabilityDefinitionDomainState,
} from './state';

/**
 * Project a consumer observation onto the Capability Definition domain state.
 *
 * Total over the consumer's observation type, and total over the frozen status
 * vocabulary. The match is exhaustive by construction — `matchObservation`
 * requires all four handlers, so a fifth observation variant would be a compile
 * error at this call site rather than a silent fallthrough into a default bucket.
 *
 * DETERMINISTIC: the result depends on nothing but `observation`. There is no
 * `Date.now()`, no `Math.random()`, no network, no browser state and no hidden
 * global state anywhere on this path.
 */
export function projectCapabilityDefinition(
  observation: ReadModelObservation<CapabilityDefinitionSnapshotV1>,
): CapabilityDefinitionDomainState {
  return matchObservation<CapabilityDefinitionSnapshotV1, CapabilityDefinitionDomainState>(
    observation,
    {
      // The contract resolved to AVAILABLE: snapshot data was retrieved. The
      // domain conclusion is about the READ — the definition is readable — and
      // says nothing about the capability's operational state.
      onAvailable: (snapshot, freshness) => definitionReadable(snapshot, freshness),
      // The contract resolved to UNKNOWN: a normal absence of data, which stays
      // its own state and is never rendered as empty, as an error or as a default.
      onUnknown: (freshness) => definitionUnknown(freshness),
      // The reader could not supply a trustworthy response. No diagnostic is
      // available to carry, and none is synthesised.
      onUnavailable: (freshness) => definitionReadUnavailable(freshness),
      // A frontend condition. NOT a contract outcome, and deliberately NOT mapped
      // to `DEFINITION_READ_UNAVAILABLE`: that would report a frontend fault as
      // "a functioning reader could not supply a trustworthy response", which is a
      // different and false statement (FE-1 Stage Work Item §7.5).
      onFrontendCondition: (condition) => definitionReadNotCompleted(condition),
    },
  );
}
